import { articlePath, coursePath } from 'libs/paths'
import { Article, Course } from 'types/data'
import { ArticleSubsections } from 'types/data/Article'

interface Instructor {
  instructorName: string | null
  headline: string | null
  primary1x1: string | null
  slug: string
}

export const mapInstructorToAsideItem = (instructor: Instructor) => ({
  title: instructor.instructorName,
  subtitle: instructor.headline,
  image: instructor.primary1x1,
  linkPath: coursePath(instructor.slug),
})

export const mapArticleToAsideItem = (article: Article) => ({
  title: article.title,
  image: article.heroImage?.url,
  linkPath: articlePath(article.slug),
})

export const mapArticleToLargeAsideItem = (article: Article) => ({
  title: article.title,
  image: article.heroImage?.url,
  linkPath: articlePath(article.slug),
  subtitle: article.description,
  date: article.creationDate,
})

export const mapArticleSubsectionsToNavItems = (
  article: Article,
  course: Course,
) => {
  const items = article.articleSubsections?.map((subsection) => ({
    label: subsection.title,
    path: `#${subsection.id}`,
  }))

  if (course && items) {
    items.push({
      label: course.title,
      path: coursePath(article.featuredCourseSlug),
    })
  }

  return items
}

export const mapLatestArticlesToArticleItems = (articles: Article[]) =>
  articles
    .filter((item) => item.heroImage && item.heroImage.url)
    .map(({ heroImage, slug, title, description, creationDate }) => ({
      linkPath: articlePath(slug),
      image: heroImage?.url,
      subtitle: description,
      date: creationDate,
      slug,
      title,
    }))

export const SubsectionEntities = {
  recipe: 'recipeSubsection',
  subsection: 'subSection',
}

export const getRecipeSubsection = (articleSubsections: ArticleSubsections[]) =>
  articleSubsections.filter(
    (sub) => sub.entityType === SubsectionEntities.recipe,
  )[0]

const iso8601DurationRegex =
  /(-)?P(?:([.,\d]+)Y)?(?:([.,\d]+)M)?(?:([.,\d]+)W)?(?:([.,\d]+)D)?T(?:([.,\d]+)H)?(?:([.,\d]+)M)?(?:([.,\d]+)S)?/

export const parseISO8601Duration = (iso8601Duration: string) => {
  const matches = iso8601Duration.match(iso8601DurationRegex)

  return {
    years: matches?.[2] || 0,
    months: matches?.[3] || 0,
    weeks: matches?.[4] || 0,
    days: matches?.[5] || 0,
    hr: matches?.[6] || 0,
    min: matches?.[7] || 0,
    sec: matches?.[8] || 0,
  }
}

export const formatIso8601Duration = (date: Date) =>
  Object.entries(date)
    .filter((elem) => elem[1])
    .reduce((prev, curr) => `${prev}${curr[1]} ${curr[0]} `, '')
    .trim()
